import React, { useState, useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { AlertTriangle, FileSearch, X, ChevronRight } from 'lucide-react';

// Gap types as defined in PRD
export type GapType = 'missing_requirement' | 'weaker_language' | 'ambiguous' | 'outdated';

// Severity levels
export type Severity = 'critical' | 'high' | 'medium' | 'low';

// Gap type configuration
export const GAP_TYPE_CONFIG: Record<GapType, { label: string; shortLabel: string; index: number }> = {
  missing_requirement: { label: 'Missing Requirement', shortLabel: 'Missing', index: 0 },
  weaker_language: { label: 'Weaker Language', shortLabel: 'Weaker', index: 1 },
  ambiguous: { label: 'Ambiguous', shortLabel: 'Ambig.', index: 2 },
  outdated: { label: 'Outdated', shortLabel: 'Outdated', index: 3 },
};

// Gap type array for X-axis
export const GAP_TYPES: GapType[] = ['missing_requirement', 'weaker_language', 'ambiguous', 'outdated'];

// Cell data for the matrix
export interface MatrixCell {
  articleId: string;
  articleNumber: string;
  articleTitle: string;
  gapType: GapType;
  gapCount: number;
  gaps: {
    id: string;
    severity: Severity;
    description: string;
  }[];
  severityCounts: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

// Article data for Y-axis
export interface ArticleInfo {
  id: string;
  number: string;
  title: string;
}

// Props for the component
export interface GapAnalysisMatrixProps {
  cells: MatrixCell[];
  articles: ArticleInfo[];
  onCellClick?: (cell: MatrixCell) => void;
  onDrillDown?: (cell: MatrixCell) => void;
  testId?: string;
}

// Get intensity color based on gap count and severity - using design system colors
const getIntensityColor = (gapCount: number, severityCounts: MatrixCell['severityCounts']): string => {
  if (gapCount === 0) return '#f5f5f5'; // neutral-100

  // Prioritize by severity - using design system danger colors
  if (severityCounts.critical > 0) {
    // Danger shades based on count
    if (gapCount >= 3) return '#dc2626'; // danger-600
    if (gapCount >= 2) return '#ef4444'; // danger-500
    return '#f87171'; // danger-400
  }
  if (severityCounts.high > 0) {
    // Warning shades based on count
    if (gapCount >= 3) return '#d97706'; // warning-600
    if (gapCount >= 2) return '#f59e0b'; // warning-500
    return '#fbbf24'; // warning-400
  }
  if (severityCounts.medium > 0) {
    // Warning lighter shades based on count
    if (gapCount >= 3) return '#f59e0b'; // warning-500
    if (gapCount >= 2) return '#fbbf24'; // warning-400
    return '#fcd34d'; // warning-300
  }
  // Low severity - primary blue shades
  if (gapCount >= 3) return '#0284c7'; // primary-600
  if (gapCount >= 2) return '#0ea5e9'; // primary-500
  return '#38bdf8'; // primary-400
};

// Custom tooltip component
interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: any[];
  articles: ArticleInfo[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, articles }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  const gapTypeConfig = GAP_TYPE_CONFIG[data.gapType as GapType];
  const article = articles.find(a => a.id === data.articleId);

  if (data.gapCount === 0) {
    return (
      <div className="chart-tooltip max-w-xs" data-testid="matrix-tooltip-empty">
        <div className="text-sm text-neutral-500">
          No {gapTypeConfig.label.toLowerCase()} gaps in Article {article?.number || data.articleNumber}
        </div>
      </div>
    );
  }

  return (
    <div className="chart-tooltip max-w-sm" data-testid="matrix-tooltip">
      <div className="mb-2">
        <div className="chart-tooltip-header">
          Article {article?.number || data.articleNumber}
        </div>
        <div className="text-sm text-neutral-600 line-clamp-1">
          {article?.title || data.articleTitle}
        </div>
      </div>

      <div className="mb-3">
        <span className="badge-neutral">
          {gapTypeConfig.label}
        </span>
      </div>

      <div className="mb-3">
        <span className="chart-tooltip-value text-neutral-900">{data.gapCount}</span>
        <span className="text-sm text-neutral-500 ml-1">gap{data.gapCount !== 1 ? 's' : ''}</span>
      </div>

      {/* Severity breakdown */}
      <div className="flex flex-wrap gap-2 mb-3">
        {data.severityCounts.critical > 0 && (
          <span className="chart-badge-critical">
            {data.severityCounts.critical} Critical
          </span>
        )}
        {data.severityCounts.high > 0 && (
          <span className="chart-badge-high">
            {data.severityCounts.high} High
          </span>
        )}
        {data.severityCounts.medium > 0 && (
          <span className="chart-badge-medium">
            {data.severityCounts.medium} Medium
          </span>
        )}
        {data.severityCounts.low > 0 && (
          <span className="chart-badge-low">
            {data.severityCounts.low} Low
          </span>
        )}
      </div>

      {/* Preview gaps */}
      {data.gaps && data.gaps.length > 0 && (
        <div className="chart-tooltip-divider">
          <div className="chart-tooltip-label">Top gaps:</div>
          {data.gaps.slice(0, 2).map((gap: any) => (
            <div key={gap.id} className="text-xs text-neutral-600 line-clamp-1 mb-1">
              • {gap.description}
            </div>
          ))}
          {data.gaps.length > 2 && (
            <div className="text-xs text-neutral-400">+{data.gaps.length - 2} more</div>
          )}
        </div>
      )}

      <div className="chart-tooltip-footer">
        Click to view details
      </div>
    </div>
  );
};

// Drill-down modal component
interface DrillDownModalProps {
  cell: MatrixCell;
  articles: ArticleInfo[];
  onClose: () => void;
  onViewAll: () => void;
}

const DrillDownModal: React.FC<DrillDownModalProps> = ({ cell, articles, onClose, onViewAll }) => {
  const gapTypeConfig = GAP_TYPE_CONFIG[cell.gapType];
  const article = articles.find(a => a.id === cell.articleId);

  const severityColors: Record<Severity, string> = {
    critical: 'bg-danger-100 text-danger-700 border-danger-200',
    high: 'bg-warning-100 text-warning-700 border-warning-200',
    medium: 'bg-warning-100 text-warning-600 border-warning-200',
    low: 'bg-primary-100 text-primary-700 border-primary-200',
  };

  return (
    <div
      className="chart-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="drilldown-modal"
    >
      <div className="chart-modal max-h-[80vh]">
        {/* Header */}
        <div className="chart-modal-header flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Article {article?.number || cell.articleNumber} - {gapTypeConfig.label}
            </h3>
            <p className="text-sm text-neutral-500 mt-1">
              {article?.title || cell.articleTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost p-1 rounded"
            data-testid="drilldown-close"
          >
            <X className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Content */}
        <div className="chart-modal-content">
          {/* Summary */}
          <div className="flex items-center gap-4 mb-4 p-3 bg-neutral-50 rounded-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-neutral-900">{cell.gapCount}</div>
              <div className="text-xs text-neutral-500">Total Gaps</div>
            </div>
            <div className="flex-1 flex gap-2 flex-wrap">
              {cell.severityCounts.critical > 0 && (
                <span className="chart-badge-critical" data-testid="drilldown-critical">
                  {cell.severityCounts.critical} Critical
                </span>
              )}
              {cell.severityCounts.high > 0 && (
                <span className="chart-badge-high" data-testid="drilldown-high">
                  {cell.severityCounts.high} High
                </span>
              )}
              {cell.severityCounts.medium > 0 && (
                <span className="chart-badge-medium" data-testid="drilldown-medium">
                  {cell.severityCounts.medium} Medium
                </span>
              )}
              {cell.severityCounts.low > 0 && (
                <span className="chart-badge-low" data-testid="drilldown-low">
                  {cell.severityCounts.low} Low
                </span>
              )}
            </div>
          </div>

          {/* Gap list */}
          <div className="space-y-3">
            {cell.gaps.map((gap, index) => (
              <div
                key={gap.id}
                className={`p-3 rounded-lg border ${severityColors[gap.severity]}`}
                data-testid={`drilldown-gap-${index}`}
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium capitalize">{gap.severity} Severity</div>
                    <p className="text-sm mt-1">{gap.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="chart-modal-footer flex items-center justify-between">
          <button
            onClick={onClose}
            className="btn-ghost"
            data-testid="drilldown-cancel"
          >
            Close
          </button>
          <button
            onClick={onViewAll}
            className="btn-primary"
            data-testid="drilldown-view-all"
          >
            View All in Gap Findings
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Legend component
const Legend: React.FC = () => (
  <div className="chart-legend" data-testid="matrix-legend">
    <span className="chart-legend-title">Intensity:</span>
    <div className="chart-legend-item">
      <div className="chart-legend-square bg-neutral-100 border border-neutral-200" />
      <span className="text-neutral-500">None</span>
    </div>
    <div className="chart-legend-item">
      <div className="chart-legend-square bg-primary-400" />
      <span className="chart-legend-label">Low</span>
    </div>
    <div className="chart-legend-item">
      <div className="chart-legend-square bg-warning-400" />
      <span className="chart-legend-label">Medium</span>
    </div>
    <div className="chart-legend-item">
      <div className="chart-legend-square bg-warning-600" />
      <span className="chart-legend-label">High</span>
    </div>
    <div className="chart-legend-item">
      <div className="chart-legend-square bg-danger-500" />
      <span className="chart-legend-label">Critical</span>
    </div>
  </div>
);

// Main component
export const GapAnalysisMatrix: React.FC<GapAnalysisMatrixProps> = ({
  cells,
  articles,
  onCellClick,
  onDrillDown,
  testId = 'gap-analysis-matrix',
}) => {
  const [selectedCell, setSelectedCell] = useState<MatrixCell | null>(null);

  // Transform cells to scatter data format
  const scatterData = useMemo(() => {
    return cells.map(cell => ({
      ...cell,
      x: GAP_TYPE_CONFIG[cell.gapType].index + 0.5, // Center in column
      y: articles.findIndex(a => a.id === cell.articleId) + 0.5, // Center in row
    }));
  }, [cells, articles]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const byType: Record<GapType, number> = {
      missing_requirement: 0,
      weaker_language: 0,
      ambiguous: 0,
      outdated: 0,
    };

    let total = 0;
    let critical = 0;
    let high = 0;

    cells.forEach(cell => {
      byType[cell.gapType] += cell.gapCount;
      total += cell.gapCount;
      critical += cell.severityCounts.critical;
      high += cell.severityCounts.high;
    });

    return { byType, total, critical, high };
  }, [cells]);

  const handleCellClick = (data: any) => {
    const cell = cells.find(
      c => c.articleId === data.articleId && c.gapType === data.gapType
    );
    if (cell && cell.gapCount > 0) {
      setSelectedCell(cell);
      onCellClick?.(cell);
    }
  };

  const handleDrillDown = () => {
    if (selectedCell) {
      onDrillDown?.(selectedCell);
      setSelectedCell(null);
    }
  };

  const handleCloseModal = () => {
    setSelectedCell(null);
  };

  // Custom shape for cells (rounded rectangles)
  const renderCell = (props: any) => {
    const { cx, cy, payload } = props;
    const cellWidth = 80;
    const cellHeight = 35;
    const color = getIntensityColor(payload.gapCount, payload.severityCounts);

    return (
      <g>
        <rect
          x={cx - cellWidth / 2}
          y={cy - cellHeight / 2}
          width={cellWidth}
          height={cellHeight}
          fill={color}
          rx={6}
          ry={6}
          stroke={payload.gapCount > 0 ? '#e5e5e5' : 'transparent'}
          strokeWidth={1}
          style={{ cursor: payload.gapCount > 0 ? 'pointer' : 'default' }}
          data-testid={`matrix-cell-${payload.articleId}-${payload.gapType}`}
        />
        {payload.gapCount > 0 && (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fill={payload.severityCounts.critical > 0 || payload.severityCounts.high > 0 ? 'white' : '#404040'}
            fontSize={14}
            fontWeight={600}
          >
            {payload.gapCount}
          </text>
        )}
      </g>
    );
  };

  if (cells.length === 0 || articles.length === 0) {
    return (
      <div
        className="chart-container chart-empty"
        data-testid={`${testId}-empty`}
      >
        <div className="chart-empty-icon">
          <FileSearch className="h-8 w-8 text-neutral-400" />
        </div>
        <h3 className="chart-empty-title">No Gap Analysis Data</h3>
        <p className="chart-empty-description">
          Run a gap analysis to view the gap distribution matrix.
        </p>
      </div>
    );
  }

  return (
    <div className="chart-container" data-testid={testId}>
      {/* Header */}
      <div className="chart-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="chart-title">
              <FileSearch className="h-5 w-5 text-primary-600" />
              Gap Analysis Matrix
            </h3>
            <p className="chart-subtitle">
              Click on a cell to view gap details for that article and gap type
            </p>
          </div>

          {/* Summary stats */}
          <div className="chart-summary" data-testid="matrix-summary">
            <span className="chart-summary-stat">
              <span className="chart-summary-value">{summaryStats.total}</span> Total Gaps
            </span>
            <div className="flex items-center gap-2">
              {summaryStats.critical > 0 && (
                <span className="chart-badge-critical" data-testid="matrix-summary-critical">
                  {summaryStats.critical} Critical
                </span>
              )}
              {summaryStats.high > 0 && (
                <span className="chart-badge-high" data-testid="matrix-summary-high">
                  {summaryStats.high} High
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Matrix Chart */}
      <div className="chart-content">
        <div className="overflow-x-auto scrollbar-thin">
          <div style={{ minWidth: '500px', height: Math.max(articles.length * 50 + 80, 300) }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                margin={{ top: 20, right: 20, bottom: 60, left: 150 }}
              >
                <XAxis
                  type="number"
                  domain={[0, GAP_TYPES.length]}
                  ticks={[0.5, 1.5, 2.5, 3.5]}
                  tickFormatter={(value) => {
                    const index = Math.floor(value);
                    const type = GAP_TYPES[index];
                    return type ? GAP_TYPE_CONFIG[type].shortLabel : '';
                  }}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#525252' }}
                />
                <YAxis
                  type="number"
                  domain={[0, articles.length]}
                  ticks={articles.map((_, i) => i + 0.5)}
                  tickFormatter={(value) => {
                    const index = Math.floor(value);
                    const article = articles[index];
                    return article ? `Art. ${article.number}` : '';
                  }}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#525252' }}
                  width={140}
                />
                <ZAxis range={[400, 400]} />
                <Tooltip
                  content={<CustomTooltip articles={articles} />}
                  cursor={false}
                />
                <Scatter
                  data={scatterData}
                  shape={renderCell}
                  onClick={handleCellClick}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* X-axis labels (full text) */}
        <div className="flex justify-center gap-8 mt-4 flex-wrap">
          {GAP_TYPES.map((type) => (
            <div
              key={type}
              className="text-xs text-neutral-500 text-center"
              data-testid={`matrix-x-label-${type}`}
            >
              <div className="font-medium text-neutral-700">{GAP_TYPE_CONFIG[type].label}</div>
              <div className="text-primary-600 font-semibold">{summaryStats.byType[type]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="chart-footer">
        <Legend />
      </div>

      {/* Drill-down modal */}
      {selectedCell && (
        <DrillDownModal
          cell={selectedCell}
          articles={articles}
          onClose={handleCloseModal}
          onViewAll={handleDrillDown}
        />
      )}
    </div>
  );
};

export default GapAnalysisMatrix;
